/* eslint-disable no-console */
import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { SolveCornerPlacementModelMode, type SolveCornerPlacementResponse } from 'api/models';
import { useSolveCornerPlacement } from 'api/queries/forming/forming';
import { buildSolveCornerPlacementRequest } from 'app/modules/wallforming/components/setting-container/corner-placement/utils/buildSolveCornerPlacementRequest';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { EMPTY_GUID } from 'shared/constants';
import { toastNotifications } from 'shared/utils/toastNotifications';

import type { ArticleBatchEntry } from 'app/modules/wallforming/components/scenes/objects/articles/types/articleBatchEntry';

interface CornerPlacementCacheEntry {
    response: SolveCornerPlacementResponse;
    errorShown: boolean;
}

interface AutomatedCornerPlacementProps {
    setLoadingState?: (isLoading: boolean) => void;
}

const MAX_CORNER_PLACEMENT_CACHE_ENTRIES = 10;

const addSolutionArticlesToStore = (
    solutions: SolveCornerPlacementResponse['solutions'],
    batchAddArticles: ReturnType<typeof usePlacementStore.getState>['batchAddArticles']
) => {
    if (!solutions) {
        return;
    }

    const articlesById = new Map<string, ArticleBatchEntry>();

    for (const solution of solutions) {
        for (const placement of solution.placements) {
            articlesById.set(placement.articleGuid, {
                id: placement.articleGuid,
                articleNumber: placement.articleNumber,
                lifeCycleCode: placement.lifeCycleCode
            });
        }
    }

    if (articlesById.size > 0) {
        batchAddArticles([...articlesById.values()]);
    }
};

export const useGetAutomatedCornerPlacement = ({ setLoadingState }: AutomatedCornerPlacementProps) => {
    const { t } = useTranslation();
    const solveCornerPlacementMutation = useSolveCornerPlacement();
    const isPendingRef = useRef<boolean>(false);
    const responseCacheRef = useRef<Map<string, CornerPlacementCacheEntry>>(new Map());

    const [searchParams] = useSearchParams();
    const sessionguid = useMemo(() => searchParams.get('formingsession') ?? EMPTY_GUID, [searchParams]);
    const sessionGuidRef = useRef(sessionguid);
    useLayoutEffect(() => {
        sessionGuidRef.current = sessionguid;
    }, [sessionguid]);

    return useCallback(async () => {
        if (isPendingRef.current) {
            return;
        }

        const state = usePlacementStore.getState();
        const selectedPoiIndex = state.poiSelection.current.index;
        if (selectedPoiIndex === undefined) {
            return;
        }

        isPendingRef.current = true;
        setLoadingState?.(true);

        try {
            const payload = buildSolveCornerPlacementRequest({
                state,
                sessionId: sessionguid,
                systemId: import.meta.env.VITE_DEFAULT_SYSTEM_ID,
                poiIndex: selectedPoiIndex,
                mode: 'Automatic'
            });
            const cacheKey = JSON.stringify(payload);
            const cachedEntry = responseCacheRef.current.get(cacheKey);
            if (cachedEntry) {
                responseCacheRef.current.delete(cacheKey);
                responseCacheRef.current.set(cacheKey, cachedEntry);
                if (cachedEntry.response.accepted) {
                    const solutions = cachedEntry.response.solutions ?? [];
                    addSolutionArticlesToStore(solutions, state.batchAddArticles);
                    state.setPlacementSolutions(solutions);
                } else if (!cachedEntry.errorShown) {
                    toastNotifications.error({
                        title: t('error'),
                        message: `${cachedEntry.response.errorCode}: ${cachedEntry.response.message}`
                    });
                    cachedEntry.errorShown = true;
                }
                return;
            }

            const response = await solveCornerPlacementMutation.mutateAsync({
                data: payload
            });
            responseCacheRef.current.set(cacheKey, { response, errorShown: false });
            if (responseCacheRef.current.size > MAX_CORNER_PLACEMENT_CACHE_ENTRIES) {
                const oldestCacheKey = responseCacheRef.current.keys().next().value;
                if (oldestCacheKey !== undefined) {
                    responseCacheRef.current.delete(oldestCacheKey);
                }
            }

            const currentState = usePlacementStore.getState();
            const currentPoiIndex = currentState.poiSelection.current.index;
            if (
                sessionGuidRef.current !== sessionguid ||
                currentPoiIndex === undefined ||
                JSON.stringify(
                    buildSolveCornerPlacementRequest({
                        state: currentState,
                        sessionId: sessionGuidRef.current,
                        systemId: import.meta.env.VITE_DEFAULT_SYSTEM_ID,
                        poiIndex: currentPoiIndex,
                        mode: SolveCornerPlacementModelMode.Automatic
                    })
                ) !== cacheKey
            ) {
                return;
            }

            if (!response.accepted) {
                toastNotifications.error({
                    title: t('error'),
                    message: `${response.errorCode}: ${response.message}`
                });
                return;
            }

            const solutions = response.solutions ?? [];
            addSolutionArticlesToStore(solutions, state.batchAddArticles);
            state.setPlacementSolutions(solutions);
            toastNotifications.success({ title: t('success'), message: response.message });
        } catch (error) {
            console.error('Automated corner placement request failed', error);
            toastNotifications.error({ title: t('error'), message: t('errorAutoCornerPlacement') });
        } finally {
            isPendingRef.current = false;
            setLoadingState?.(false);
        }
    }, [sessionguid, setLoadingState, solveCornerPlacementMutation, t]);
};
